import React, { FC, useMemo, useState } from 'react';
import { Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { JobPartsTable } from './job-parts-table';

const useDMSDetailStyles = createUseStyles({
    title: {
        display: 'flex',
        flex: 1,
        padding: '24px 30px',
    },
    flexBasis50: {
        flexBasis: '50%',
    },
    jobBlock: {
        marginBottom: 16,
    },
    jobIntro: {
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
    },
    labelValuePair: {
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
    },
    partsBlock: {
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
        width: '100%',
    },
    partsWrapper: {
        width: '100%',
        marginTop: 8,
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
    },
    partsHeaderCell: {
        backgroundColor: '#F8F8F8',
        borderBottom: '1px solid #E3E3E3',
        '&:not(:last-child)': {
            borderRight: '1px solid #E3E3E3',
        },
    },
    metaContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        padding: '0px 30px 0px 30px',
        columnGap: 32,
        rowGap: 12,
    },
});

interface DMSJobDetailProps {
    details: {
        salesType: string;
        complaint: string;
        correction: string;
        cause: string | null | undefined;
        parts: {
            partNumber: string;
            partDescription: string;
            partQuantity: number;
        }[];
        technicians: any[];
    }[];
}

export const DMSJobDetail: FC<DMSJobDetailProps> = ({ details }) => {
    const classes = useDMSDetailStyles();
    const { t } = useTranslation();

    const salesTypeMap: Record<string, string> = {
        C: 'Customer Pay',
        W: 'Warranty',
        I: 'Internal',
    };

    const getJobMeta = (job: DMSJobDetailProps['details'][number]) => ({
        intro: [
            { label: 'Complaint', value: job.complaint ?? 'N/A' },
            { label: 'Cause', value: job.cause ?? 'N/A' },
        ],
        rest: [
            { label: 'Correction', value: job.correction ?? 'N/A' },
            { label: 'Sales Type', value: salesTypeMap[job.salesType] ?? job.salesType ?? 'N/A' },
        ],
    });

    return (
        <>
            {details.map((job, index) => (
                <div key={index} className={classes.jobBlock}>
                    {(() => {
                        const meta = getJobMeta(job);
                        return (
                            <>
                                <div className={classes.jobIntro}>
                                    <div className={classes.title}>
                                        <Typography variant="h3">{t(`Job #${index + 1}`)}</Typography>
                                    </div>
                                    <div className={classes.metaContainer}>
                                        {meta.intro.map(({ label, value }, i) => (
                                            <div
                                                key={i}
                                                className={`d-flex flex-column pb-6 ${classes.labelValuePair}`}
                                            >
                                                <div>
                                                    <Typography variant="label">{label}</Typography>
                                                </div>
                                                <div className="pt-1">
                                                    <Typography>{value}</Typography>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className={classes.metaContainer}>
                                    {meta.rest.map(({ label, value }, i) => (
                                        <div key={i} className={`d-flex flex-column pb-6 ${classes.labelValuePair}`}>
                                            <div>
                                                <Typography variant="label">{label}</Typography>
                                            </div>
                                            <div className="pt-1">
                                                <Typography>{value}</Typography>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        );
                    })()}
                    <div style={{ padding: '0 30px' }}>
                        <div className={classes.partsBlock}>
                            {job.parts.length === 0 ? (
                                <div className={`pb-6 ${classes.labelValuePair}`}>
                                    <Typography variant="label">Parts: No Parts used for this job.</Typography>
                                </div>
                            ) : (
                                <>
                                    <div className={`pb-2 ${classes.labelValuePair}`}>
                                        <Typography variant="label">Parts:</Typography>
                                    </div>
                                    <div className={classes.partsWrapper}>
                                        <JobPartsTable parts={job.parts} />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div style={{ padding: '16px 30px 0 30px' }} className={classes.labelValuePair}>
                        <div style={{ border: '1px solid #E3E3E3' }} />
                    </div>
                </div>
            ))}
        </>
    );
};
